import { AlertBox } from '@components/ui';
import type { GlobalInsight } from '@hooks/useInsights';
import type { ReactElement } from 'react';

interface GlobalInsightCardProps {
  insight: GlobalInsight;
  totalBets: number;
}

export function GlobalInsightCard({ insight, totalBets }: GlobalInsightCardProps): ReactElement {
  const content = insight.content || {};

  // Render stats-based insight (like parlay odds)
  if (content.stats) {
    return (
      <AlertBox variant={insight.variant} title={insight.title}>
        <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3 text-sm">
          {content.stats.map((stat, idx) => (
            <div key={idx} className="bg-theme-surface rounded p-2 text-center">
              <div
                className={`text-xl font-bold ${stat.highlight ? 'text-theme-danger' : 'text-white'}`}
              >
                {stat.value || stat.winRate}
              </div>
              <div className="text-theme-text-tertiary">{stat.label || stat.description}</div>
            </div>
          ))}
        </div>
        {content.note && <p className="text-theme-text-tertiary text-xs mt-3">{content.note}</p>}
        {insight.category === 'benchmarks' && totalBets > 0 && totalBets < 100 && (
          <p className="text-theme-text-tertiary text-xs mt-3">
            Your {totalBets} bets is a small sample. Keep tracking for reliable conclusions!
          </p>
        )}
      </AlertBox>
    );
  }

  // Render tips-based insight
  if (content.tips) {
    return (
      <AlertBox variant={insight.variant} title={insight.title}>
        <ul className="space-y-2 text-sm">
          {content.tips.map((tip, idx) => (
            <li key={idx}>
              <strong>{tip.label}:</strong> {tip.text}
            </li>
          ))}
        </ul>
      </AlertBox>
    );
  }

  // Fallback for unknown format
  return (
    <AlertBox variant={insight.variant} title={insight.title}>
      <pre className="text-xs text-theme-text-tertiary">{JSON.stringify(content, null, 2)}</pre>
    </AlertBox>
  );
}
